const MUST_BE_NUMBER = 'Must be a number';

// Minimum Purchase Requirements Types
// NO_REQUIREMENTS: No minimum purchase requirements
// AMOUNT: Minimum purchase amount
// QUANTITY: Minimum purchase quantity
export const MIN_PURCHASE_REQUIREMENTS = {
  NO_REQUIREMENTS: 'NO_REQUIREMENTS',
  AMOUNT: 'AMOUNT',
  QUANTITY: 'QUANTITY'
};

// API Types for Minimum Purchase Requirements
// MIN_PURCHASE_AMOUNT: Minimum purchase amount
// MIN_ITEMS_QUANTITY: Minimum purchase quantity
// NO_MIN_REQUIREMENTS: No minimum purchase requirements
export const MIN_PURCHASE_REQUIREMENTS_API_TYPES = {
  MIN_PURCHASE_AMOUNT: 'MIN_PURCHASE_AMOUNT',
  MIN_ITEMS_QUANTITY: 'MIN_ITEMS_QUANTITY',
  NO_MIN_REQUIREMENTS: 'NO_MIN_REQUIREMENTS'
};

// Maximum Discount Uses Types
// UNLIMITED: Unlimited discount uses
// LIMITED: Limited discount uses
export const MAX_DISCOUNT_USES = {
  UNLIMITED: 'UNLIMITED',
  LIMITED: 'LIMITED'
};

// API Types for Maximum Discount Uses
// TOTAL_DISCOUNT_USAGE: Total discount usage
// SINGLE_USE_PER_CUSTOMER: Single use per customer
export const MAX_DISCOUNT_USES_API_TYPES = {
  TOTAL_DISCOUNT_USAGE: 'total_discount_usage',
  SINGLE_USE_PER_CUSTOMER: 'single_use_per_customer'
};

// End Date Unit
// DAYS: Days
export const END_DATE_UNIT = {
  DAYS: 'days'
};

// Form Field Names
// MIN_PURCHASE_REQUIREMENTS: Minimum purchase requirements
// MIN_PURCHASE_AMOUNT: Minimum purchase amount
// MIN_PURCHASE_QUANTITY: Minimum purchase quantity
// MAX_DISCOUNT_USES: Maximum discount uses
// MAX_DISCOUNT_USES_COUNT: Maximum discount uses count
// MAX_DISCOUNT_USES_PER_CUSTOMER: Maximum discount uses per customer
// END_DATE: End date
export const FORM_FIELDS = {
  MIN_PURCHASE_REQUIREMENTS: 'min_purchase_requirements',
  MIN_PURCHASE_AMOUNT: 'min_purchase_amount',
  MIN_PURCHASE_QUANTITY: 'min_purchase_quantity',
  MAX_DISCOUNT_USES: 'max_discount_uses',
  MAX_DISCOUNT_USES_COUNT: 'max_discount_uses_count',
  MAX_DISCOUNT_USES_PER_CUSTOMER: 'max_discount_uses_per_customer',
  END_DATE: 'end_date'
};

// Default Values
// ONBOARDING: Onboarding default values
// REGULAR: Regular default values
export const DEFAULT_VALUES = {
  ONBOARDING: {
    [FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS]: MIN_PURCHASE_REQUIREMENTS.NO_REQUIREMENTS,
    [FORM_FIELDS.MIN_PURCHASE_AMOUNT]: '',
    [FORM_FIELDS.MIN_PURCHASE_QUANTITY]: '',
    [FORM_FIELDS.MAX_DISCOUNT_USES]: MAX_DISCOUNT_USES.LIMITED,
    [FORM_FIELDS.MAX_DISCOUNT_USES_COUNT]: '1',
    [FORM_FIELDS.MAX_DISCOUNT_USES_PER_CUSTOMER]: true,
    [FORM_FIELDS.END_DATE]: ''
  },
  REGULAR: {
    [FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS]: MIN_PURCHASE_REQUIREMENTS.NO_REQUIREMENTS,
    [FORM_FIELDS.MIN_PURCHASE_AMOUNT]: '',
    [FORM_FIELDS.MIN_PURCHASE_QUANTITY]: '',
    [FORM_FIELDS.MAX_DISCOUNT_USES]: MAX_DISCOUNT_USES.LIMITED,
    [FORM_FIELDS.MAX_DISCOUNT_USES_COUNT]: '1',
    [FORM_FIELDS.MAX_DISCOUNT_USES_PER_CUSTOMER]: true,
    [FORM_FIELDS.END_DATE]: ''
  }
};

// Validation Messages
// MIN_PURCHASE_REQUIREMENT_REQUIRED: Minimum purchase requirement is required
// MIN_PURCHASE_AMOUNT_REQUIRED: Minimum purchase amount is required
// MIN_PURCHASE_AMOUNT_NUMBER: Must be a number
// MIN_PURCHASE_AMOUNT_MIN: Amount must be at least 0
// MIN_PURCHASE_QUANTITY_REQUIRED: Minimum purchase quantity is required
// MIN_PURCHASE_QUANTITY_INTEGER: Quantity must be an integer
// MIN_PURCHASE_QUANTITY_MIN: Quantity must be at least 1
// MAX_DISCOUNT_USES_REQUIRED: Maximum uses setting is required
// MAX_DISCOUNT_USES_COUNT_REQUIRED: Maximum discount uses count is required
// MAX_DISCOUNT_USES_COUNT_NUMBER: Must be a number
// MAX_DISCOUNT_USES_COUNT_INTEGER: Count must be an integer
// MAX_DISCOUNT_USES_COUNT_MIN: Count must be at least 1
// END_DATE_NUMBER: Must be a number
// END_DATE_POSITIVE_INTEGER: Must be a positive whole number greater than 0
export const VALIDATION_MESSAGES = {
  MIN_PURCHASE_REQUIREMENT_REQUIRED: 'Minimum purchase requirement is required',
  MIN_PURCHASE_AMOUNT_REQUIRED: 'Minimum purchase amount is required',
  MIN_PURCHASE_AMOUNT_NUMBER: MUST_BE_NUMBER,
  MIN_PURCHASE_AMOUNT_MIN: 'Amount must be at least 0',
  MIN_PURCHASE_QUANTITY_REQUIRED: 'Minimum purchase quantity is required',
  MIN_PURCHASE_QUANTITY_INTEGER: 'Quantity must be an integer',
  MIN_PURCHASE_QUANTITY_MIN: 'Quantity must be at least 1',
  MAX_DISCOUNT_USES_REQUIRED: 'Maximum uses setting is required',
  MAX_DISCOUNT_USES_COUNT_REQUIRED: 'Maximum discount uses count is required',
  MAX_DISCOUNT_USES_COUNT_NUMBER: MUST_BE_NUMBER,
  MAX_DISCOUNT_USES_COUNT_INTEGER: 'Count must be an integer',
  MAX_DISCOUNT_USES_COUNT_MIN: 'Count must be at least 1',
  END_DATE_NUMBER: MUST_BE_NUMBER,
  END_DATE_POSITIVE_INTEGER: 'Must be a positive whole number greater than 0'
};

// UI Labels and Text
// MODAL_TITLE: Configure Discount Options
// BUTTON_CANCEL: Cancel
// BUTTON_SAVE_CONFIGURATION: Save Configuration
// MIN_PURCHASE_REQUIREMENTS_TITLE: Minimum Purchase Requirements
// MIN_PURCHASE_REQUIREMENTS_DESCRIPTION: Set the minimum requirement for customers to be eligible for this discount.
// MIN_PURCHASE_TYPE_LABEL: Minimum Purchase Type
// NO_MINIMUM_REQUIREMENTS: No minimum requirements
// MINIMUM_PURCHASE_AMOUNT: Minimum purchase amount ($)
// MINIMUM_QUANTITY_OF_ITEMS: Minimum quantity of items
// AMOUNT_LABEL: Amount ($)
// MINIMUM_QUANTITY_LABEL: Minimum Quantity
// MAX_DISCOUNT_USES_TITLE: Maximum Discount Uses
// MAX_DISCOUNT_USES_DESCRIPTION: Limit how many times this discount can be used.
// DISCOUNT_USAGE_LIMIT_LABEL: Discount Usage Limit
// UNLIMITED_USES: Unlimited uses
// LIMIT_NUMBER_OF_TIMES: Limit number of times this discount can be used
// TOTAL_USES_LABEL: Total Uses
// LIMIT_ONE_USE_PER_CUSTOMER: Limit to one use per customer
// VALIDITY_PERIOD_TITLE: Validity Period
// VALIDITY_PERIOD_DESCRIPTION: Set how many days from the meeting date this discount is valid. Leave blank for indefinite validity.
// NUMBER_OF_DAYS_LABEL: Number of Days
export const UI_TEXT = {
  MODAL_TITLE: 'Configure Discount Options',
  BUTTON_CANCEL: 'Cancel',
  BUTTON_SAVE_CONFIGURATION: 'Save Configuration',
  MIN_PURCHASE_REQUIREMENTS_TITLE: 'Minimum Purchase Requirements',
  MIN_PURCHASE_REQUIREMENTS_DESCRIPTION: 'Set the minimum requirement for customers to be eligible for this discount.',
  MIN_PURCHASE_TYPE_LABEL: 'Minimum Purchase Type',
  NO_MINIMUM_REQUIREMENTS: 'No minimum requirements',
  MINIMUM_PURCHASE_AMOUNT: 'Minimum purchase amount ($)',
  MINIMUM_QUANTITY_OF_ITEMS: 'Minimum quantity of items',
  AMOUNT_LABEL: 'Amount ($)',
  MINIMUM_QUANTITY_LABEL: 'Minimum Quantity',
  MAX_DISCOUNT_USES_TITLE: 'Maximum Discount Uses',
  MAX_DISCOUNT_USES_DESCRIPTION: 'Limit how many times this discount can be used.',
  DISCOUNT_USAGE_LIMIT_LABEL: 'Discount Usage Limit',
  UNLIMITED_USES: 'Unlimited uses',
  LIMIT_NUMBER_OF_TIMES: 'Limit number of times this discount can be used',
  TOTAL_USES_LABEL: 'Total Uses',
  LIMIT_ONE_USE_PER_CUSTOMER: 'Limit to one use per customer',
  VALIDITY_PERIOD_TITLE: 'Validity Period',
  VALIDITY_PERIOD_DESCRIPTION: 'Set how many days from the meeting date this discount is valid. Leave blank for indefinite validity.',
  NUMBER_OF_DAYS_LABEL: 'Number of Days'
};

// Placeholders
// AMOUNT: Amount ($)
// QUANTITY: Quantity
// TOTAL_USES: Total Uses
// END_DATE: End date
export const PLACEHOLDERS = {
  AMOUNT: '0.00',
  QUANTITY: '1',
  TOTAL_USES: '1',
  END_DATE: 'e.g., 30'
};

// Input Attributes
// AMOUNT_STEP: 0.01
// AMOUNT_MIN: 0
// QUANTITY_MIN: 1
// TOTAL_USES_MIN: 1
// END_DATE_MIN: 1
// END_DATE_STEP: 1
export const INPUT_ATTRIBUTES = {
  AMOUNT_STEP: '0.01',
  AMOUNT_MIN: '0',
  QUANTITY_MIN: '1',
  TOTAL_USES_MIN: '1',
  END_DATE_MIN: '1',
  END_DATE_STEP: '1'
};

// Modal Configuration
// WIDTH: 700
// BUTTON_KEYS: {
//   CANCEL: 'cancel',
//   SUBMIT: 'submit'
// }
// BUTTON_TYPES: {
//   PRIMARY: 'primary'
// }
export const MODAL_CONFIG = {
  WIDTH: 700,
  BUTTON_KEYS: {
    CANCEL: 'cancel',
    SUBMIT: 'submit'
  },
  BUTTON_TYPES: {
    PRIMARY: 'primary'
  }
};

// Restricted Keys for Number Inputs
// NEGATIVE: '-'
// DECIMAL: '.'
// SCIENTIFIC_E: 'e'
// SCIENTIFIC_E_UPPER: 'E'
// PLUS: '+'
export const RESTRICTED_KEYS = {
  NEGATIVE: '-',
  DECIMAL: '.',
  SCIENTIFIC_E: 'e',
  SCIENTIFIC_E_UPPER: 'E',
  PLUS: '+'
};

// Console Messages
// VALIDATION_FAILED: Validation failed:
export const CONSOLE_MESSAGES = {
  VALIDATION_FAILED: 'Validation failed:'
};

